import { Dialog } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { File } from "@tellescope/types-models";
import { isImageType, isPdfType } from "../hooks/useFilePreview";
import { PdfPreviewFrame, pdfPreviewDialogClassName } from "./PdfPreviewFrame";

interface FilePreviewDialogProps {
  /** The file being previewed */
  file: File | null;
  /** The presigned URL for the file */
  url: string | null;
  /** Whether the preview is currently loading */
  isLoading?: boolean;
  /** Callback when the dialog is closed */
  onClose: () => void;
}

/**
 * Dialog for previewing image and PDF files.
 *
 * Displays images directly and PDFs in an iframe.
 */
export function FilePreviewDialog({ file, url, isLoading = false, onClose }: FilePreviewDialogProps) {
  const { t } = useTranslation("files");
  const isOpen = !!(file && url) || isLoading;
  const isPdfPreview = !isLoading && !!file && !!url && isPdfType(file.type ?? "");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className={isPdfPreview ? pdfPreviewDialogClassName : "max-w-4xl"}>
        <Dialog.Header>
          <Dialog.Title className="truncate pr-8">{file?.name ?? t("Loading...")}</Dialog.Title>
        </Dialog.Header>

        <Dialog.Body className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : file && url ? (
            <PreviewContent file={file} url={url} />
          ) : null}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
}

interface PreviewContentProps {
  file: File;
  url: string;
}

function PreviewContent({ file, url }: PreviewContentProps) {
  const { t } = useTranslation("files");

  if (isImageType(file.type ?? "")) {
    return (
      <div className="flex items-center justify-center">
        <img src={url} alt={file.name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
      </div>
    );
  }

  if (isPdfType(file.type ?? "")) {
    return <PdfPreviewFrame url={url} title={file.name} />;
  }

  // Fallback for unsupported types (shouldn't happen if isPreviewable is used correctly)
  return (
    <div className="flex items-center justify-center h-64 text-muted-foreground">
      <p>{t("Preview not available for this file type")}</p>
    </div>
  );
}
